import type { DesignTokens } from "../types/theme";

// Cartero brand font: Outfit (loaded in apps/web/index.html), with a robust
// system fallback chain so text stays readable if the webfont is slow/offline.
const WEB_SYSTEM_FONT =
  "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode — Cartero "cream paper + ink" with a postal-stamp
 * accent (vermilion). These hex values are the runtime source (ThemeProvider
 * injects them as CSS vars at boot); keep in sync with packages/ui/vars.css.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f5f1ea",
  foreground: "#29241d",
  surface: "#fefcf8",
  surfaceForeground: "#29241d",
  surfaceSecondary: "#efe9df",
  surfaceSecondaryForeground: "#29241d",
  surfaceTertiary: "#e6dfd2",
  surfaceTertiaryForeground: "#29241d",
  overlay: "#fefcf8",
  overlayForeground: "#29241d",
  muted: "#7a7165",
  default: "#ebe6dc",
  defaultForeground: "#29241d",
  accent: "#cf5330",
  accentForeground: "#fff8f1",
  danger: "#e23d43",
  dangerForeground: "#fff8f1",
  warning: "#ec9422",
  warningForeground: "#29241d",
  success: "#2ba05f",
  successForeground: "#fff8f1",
  separator: "#cabfae",
  glassBackground: "rgba(254, 252, 248, 0.85)",
  glassBorder: "rgba(41, 36, 29, 0.1)",
  glassHighlight: "rgba(41, 36, 29, 0.08)",
  glow0: "rgba(207, 83, 48, 0.12)",
  glow1: "rgba(224, 120, 60, 0.1)",
  glow2: "rgba(242, 162, 74, 0.08)",
  mutedForeground: "#29241d",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(40, 30, 20, 0.14)",
  overlayShadow: "rgba(40, 30, 20, 0.2)",
  fieldShadow: "rgba(40, 30, 20, 0.06)",
  fieldBorder: "rgba(41, 36, 29, 0.12)",
  fieldPlaceholder: "rgba(41, 36, 29, 0.42)",
  fieldBackground: "rgba(41, 36, 29, 0.035)",
  segment: "#fefcf8",
  segmentForeground: "#29241d",
  link: "#29241d",
  focus: "#cf5330",
  fieldForeground: "#29241d",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#ddd4c6",
  primary: "#cf5330",
  primaryForeground: "#fff8f1",
  secondary: "#ebe6dc",
  secondaryForeground: "#29241d",
  backdrop: "rgba(30, 26, 20, 0.42)",
};

/**
 * Base tokens for Dark mode — Cartero "softened ink" with a postal-stamp
 * accent (a brighter vermilion for dark surfaces).
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#181310",
  foreground: "#ece4d8",
  surface: "#221d17",
  surfaceForeground: "#ece4d8",
  surfaceSecondary: "#29231c",
  surfaceSecondaryForeground: "#ece4d8",
  surfaceTertiary: "#342d24",
  surfaceTertiaryForeground: "#ece4d8",
  overlay: "#262019",
  overlayForeground: "#ece4d8",
  muted: "#b3a794",
  default: "#2e2820",
  defaultForeground: "#ece4d8",
  accent: "#ec7645",
  accentForeground: "#fff8f1",
  danger: "#f04a52",
  dangerForeground: "#fff8f1",
  warning: "#f5b23f",
  warningForeground: "#201a12",
  success: "#37b26f",
  successForeground: "#201a12",
  separator: "#453d31",
  glassBackground: "rgba(24, 19, 16, 0.985)",
  glassBorder: "rgba(255, 248, 241, 0.12)",
  glassHighlight: "rgba(255, 248, 241, 0.22)",
  glow0: "rgba(236, 118, 69, 0.42)",
  glow1: "rgba(233, 120, 60, 0.36)",
  glow2: "rgba(242, 170, 80, 0.3)",
  mutedForeground: "#ece4d8",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.4)",
  overlayShadow: "rgba(0, 0, 0, 0.55)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(255, 248, 241, 0.12)",
  fieldPlaceholder: "rgba(255, 248, 241, 0.42)",
  fieldBackground: "rgba(255, 248, 241, 0.04)",
  segment: "#352e25",
  segmentForeground: "#ece4d8",
  link: "#ece4d8",
  focus: "#ec7645",
  fieldForeground: "#ece4d8",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#453d31",
  primary: "#ec7645",
  primaryForeground: "#fff8f1",
  secondary: "#2e2820",
  secondaryForeground: "#ece4d8",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
